import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { ReassignmentFailureAlert } from "org/user/components/ReassignmentFailureAlert";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { useControlsForUserQuery } from "org/user/queries/useControlsForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { describeControlFailure } from "./describeControlFailure";
import { useRemoveControlAssignmentUtils } from "./useRemoveControlAssignmentUtils";
import { useReplaceControlUserUtils } from "./useReplaceControlUserUtils";
import {
    ControlRow,
    useUserAssignedControlListTable,
} from "./useUserAssignedControlListTable";

export function UserAssignedControlsList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { data: controls } = useControlsForUserQuery(userId);
    const { t } = useTranslation();

    const { table, getFilterComponent, canAddException, tableId } =
        useUserAssignedControlListTable(controls);

    const modeConfig = getFilterModeConfig(t);

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        removeAccessRightError,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveControlAssignmentUtils({ userId, selectedRows, deselectAll });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        replaceUserError,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceControlUserUtils({ userId, selectedRows, deselectAll });

    const removeFailure = describeControlFailure(
        t,
        removeAccessRightError,
        controls
    );
    const replaceFailure = describeControlFailure(
        t,
        replaceUserError,
        controls
    );

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={removeAccessRight}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t("AccessAndAssignment_Controls_UnassignUserMessage", {
                    user: formatUserName(user),
                    count: selectedRows.length,
                })}
                error={
                    removeFailure && (
                        <ReassignmentFailureAlert failure={removeFailure} />
                    )
                }
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUserOrGroup"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={replaceUser}
                user={user}
                error={
                    replaceFailure && (
                        <ReassignmentFailureAlert failure={replaceFailure} />
                    )
                }
            />
            <AssignmentTable<ControlRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                modeConfig={modeConfig}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t("AccessAndAssignment_Controls_UnassignUser")}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
